import { ProviderFile } from "fileParsing/types";

/**
 * Adds the second to the first array
 * passed by reference
 * should be faster then concat or push
 *
 * warning: has side effects
 *
 * @param arr1
 * @param arr2
 */
export function mergeArrays<T>(arr1: T[], arr2: T[]) {
  const arr1Length = arr1.length;
  const arr2Length = arr2.length;
  arr1.length = arr1Length + arr2Length;
  for (let i = 0; i < arr2.length; i++) {
    arr1[arr1Length + i] = arr2[i];
  }
}

/**
 * Adds "Chat with" at the beginning of the string
 *
 * Example:
 * ["Max", "Sam", "Zoe"] => "Chat with Max, Sam and Zoe"
 * @param list list of strings
 * @returns beautiful string
 */
export function beautifyParticipants(list: string[]): string {
  return "Chat with " + beautifyArray(list);
}

/**
 * Creates a nicer sounding array
 *
 * example:
 * ["Max", "Sam", "Zoe"] => "Max, Sam and Zoe"
 * @param list
 * @returns
 */
export function beautifyArray(list: string[]): string {
  if (list.length === 1) return list[0];
  const l = list.length - 1;
  return list.slice(0, l).join(", ") + ", and " + list[l];
}

/**
 * capitalizes the first letter of the given string
 * @param str string to capitalize
 * @returns
 */
export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Converts any javascript value to a nice looking string.
 * Primary different to getPrimitiveAsString is that it does
 * not insert tabs when the given value is object.
 *
 * @param d anything
 * @returns string format
 */
export function stringify(d: unknown): string {
  if (typeof d === "object" && d !== null) return getObjectAsString(d);
  else return getPrimitiveAsString(d);
}

/**
 * gets whole objects as string
 * @param obj object with some keys or something
 * @returns a string that looks a bit nice
 */
export function getObjectAsString(obj: object): string {
  return (
    Object.entries(obj)
      //.filter(d => d[1] !== null && d[1] !== '')
      .map(
        (d) =>
          `${d[0]}: ${(typeof d[1] === "object" && !Array.isArray(d[1]) ? "\n" : "") + getPrimitiveAsString(d[1])}`,
      )
      .join("\n")
  );
}

/**
 * transforms a priminitive to a string
 * @param d anything really
 * @returns returns it in string format
 */
export function getPrimitiveAsString(d: unknown): string {
  if (d === null) return "";
  else if (typeof d === "string") return d;
  else if (typeof d === "number") return String(d);
  else if (typeof d === "boolean") return d ? "True" : "False";
  else if (d instanceof Date) return d.toLocaleString();
  else if (Array.isArray(d)) {
    return `(${d.length} items)\n${d.map((v) => `${getPrimitiveAsString(v)}`).join("\n")}`;
  } else if (typeof d === "object") {
    // intent the inner objects
    return `${getObjectAsString(d)
      .split("\n")
      .map((d) => `\t${d}`)
      .join("\n")}`;
  }
  return "";
}

/**
 * Removes duplicate elements from given array.
 *
 * May cause problems if values match when converted to different types,
 * such as '1' === 1.
 * @param a Arbitrary array
 * @returns Array with only unique elements
 */
export function removeDuplicates<T>(a: T[]): T[] {
  return a.reduce<{ arr: T[]; seen: { [k: string]: number } }>(
    (res, el) =>
      !res.seen[String(el)]
        ? {
            arr: [...res.arr, el],
            seen: { ...res.seen, [String(el)]: 1 },
          }
        : res,
    { arr: [], seen: {} },
  ).arr;
}

/**
 * Transforms a number into a string
 *
 * Example:
 * * 1024 -> 1 kB
 * * 300 -> 300 B
 * * 10000 -> 10 MB
 *
 * @param bytes as number
 * @returns string
 */
export function getByteSize(bytes: number): string {
  const thresh = 1024;
  const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const r = 10 ** 1;
  let u = -1;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(1) + " " + units[u];
}

export function getDataPointsLength(data: ProviderFile[]) {
  return data.reduce((pv, cv) => pv + cv.data.length, 0);
}

/**
 * Compares two uint8Arrays. Returns true if the start of the data array is equal to the prefix array.
 *
 * @param data full array
 * @param prefix possible prefix of data array
 * @returns if their start is equal
 */
export function startsWithUint8Arrays(data: Uint8Array, prefix: Uint8Array): boolean {
  if (data.byteLength >= prefix.byteLength && data.length >= prefix.length) {
    for (let i = 0; i < prefix.length; i++) {
      if (data[i] !== prefix[i]) return false;
    }
    return true;
  }
  return false;
}
